import { useEffect, useRef, useState } from "react";
import { checkForUpdate } from "src/update/checkForUpdate";

/**
 * 탭이 다시 보일 때마다 확인하되, 잦은 탭 전환에 따른 중복 확인만 막는 스로틀
 * 마운트·폴링 확인에는 적용하지 않는다
 */
const VISIBILITY_RECHECK_THROTTLE_MS = 60 * 1000;

export interface UseUpdateAvailableOptions {
  /**
   * 폴링 주기(ms) 지정하면 이 주기로 주기적 확인을 추가
   **/
  pollIntervalMs?: number;
}

/**
 * 새 버전이 배포되었으면 true를 반환(한번 감지되면 유지)
 * 확인 시점: 마운트 시 1회 + 탭이 다시 보일 때 + (pollIntervalMs 마다)
 **/
export function useUpdateAvailable({ pollIntervalMs }: UseUpdateAvailableOptions = {}): boolean {
  const [available, setAvailable] = useState(false);
  const availableRef = useRef(false);

  useEffect(() => {
    let cancelled = false;
    let lastCheck = 0;

    const check = async () => {
      if (availableRef.current) return;
      lastCheck = Date.now();
      const has = await checkForUpdate();
      if (!cancelled && has) {
        availableRef.current = true;
        setAvailable(true);
      }
    };

    const onVisible = () => {
      if (document.visibilityState !== "visible") return;
      if (Date.now() - lastCheck < VISIBILITY_RECHECK_THROTTLE_MS) return;
      void check();
    };

    void check(); // 마운트 시 1회
    document.addEventListener("visibilitychange", onVisible);

    let timer: ReturnType<typeof setInterval> | undefined;
    if (pollIntervalMs && pollIntervalMs > 0) {
      timer = setInterval(() => void check(), pollIntervalMs);
    }

    return () => {
      cancelled = true;
      document.removeEventListener("visibilitychange", onVisible);
      if (timer) clearInterval(timer);
    };
  }, [pollIntervalMs]);

  return available;
}

/**
 * 자동 리로드 페이지용
 * 감지되면 자동 새로고침
 **/
export function useAutoReloadOnUpdate(opts: { pollIntervalMs: number; delayMs?: number }): void {
  const available = useUpdateAvailable({ pollIntervalMs: opts.pollIntervalMs });
  useEffect(() => {
    if (!available) return;
    const timer = setTimeout(() => window.location.reload(), opts.delayMs ?? 0);
    return () => clearTimeout(timer);
  }, [available, opts.delayMs]);
}
